import { useCallback, useState } from 'react';

const STORAGE_KEY_PREFIX = 'gap-review:disclosure:';

export interface StickyDisclosure {
  isOpen: boolean;
  setOpen: (isOpen: boolean) => void;
}

/**
 * A `<details>` whose open state survives the queue, and the session.
 *
 * Persisted for the same reason a pane width is (`usePaneWidth`): it is a
 * reading preference rather than a position in the queue. The orientation prose
 * repeats verbatim on every gap of a scope, so a reviewer who folded it away on
 * the first draft has folded it away for the whole run — re-opening it on each
 * of twenty drafts is the failure mode this exists to prevent.
 *
 * `defaultOpen` applies only until the reviewer touches it. Nothing is written
 * on mount, so a default can change later without every stored value having
 * already pinned the old one.
 */
export function useStickyDisclosure(
  disclosureKey: string,
  defaultOpen: boolean
): StickyDisclosure {
  const [isOpen, setIsOpen] = useState(() => {
    const stored = localStorage.getItem(
      `${STORAGE_KEY_PREFIX}${disclosureKey}`
    );
    if (stored === null) return defaultOpen;
    return stored === 'open';
  });

  const setOpen = useCallback(
    (nextOpen: boolean) => {
      setIsOpen(nextOpen);
      localStorage.setItem(
        `${STORAGE_KEY_PREFIX}${disclosureKey}`,
        nextOpen ? 'open' : 'closed'
      );
    },
    [disclosureKey]
  );

  return { isOpen, setOpen };
}
